/**
 * Tenancy API - vertical change requests.
 *
 * Mirrors backend tenancy/vertical_views.py at /api/v1/tenancy/vertical/.
 * Org users (Owner/Admin) request a change; platform admins approve/deny
 * via /api/v1/tenancy/vertical/admin/ (control-plane, not wired here yet).
 */
import { baseApi } from "@/app/store/api/baseApi";

import type {
  VerticalChangeRequest,
  VerticalChangeRequestBody,
  VerticalStatus,
} from "../model/types";

/** RTK Query endpoints for org vertical management. */
export const tenancyApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getVerticalStatus: build.query<VerticalStatus, void>({
      query: () => "/tenancy/vertical/",
      providesTags: [{ type: "VerticalChange", id: "STATUS" }],
    }),
    getTenantCover: build.query<{ cover_url: string | null }, void>({
      query: () => "/tenancy/cover/",
      providesTags: [{ type: "Tenant", id: "COVER" }],
    }),
    uploadTenantCover: build.mutation<{ cover_url: string | null }, File>({
      query: (file) => {
        const form = new FormData();
        form.append("file", file);
        return { url: "/tenancy/cover/", method: "POST", body: form };
      },
      invalidatesTags: [{ type: "Tenant", id: "COVER" }],
    }),
    deleteTenantCover: build.mutation<{ cover_url: string | null }, void>({
      query: () => ({ url: "/tenancy/cover/", method: "DELETE" }),
      invalidatesTags: [{ type: "Tenant", id: "COVER" }],
    }),
    getTenantLogo: build.query<{ logo_url: string | null }, void>({
      query: () => "/tenancy/logo/",
      providesTags: [{ type: "Tenant", id: "LOGO" }],
    }),
    uploadTenantLogo: build.mutation<{ logo_url: string | null }, File>({
      query: (file) => {
        const form = new FormData();
        form.append("file", file);
        return { url: "/tenancy/logo/", method: "POST", body: form };
      },
      invalidatesTags: [{ type: "Tenant", id: "LOGO" }, "Manifest"],
    }),
    deleteTenantLogo: build.mutation<{ logo_url: string | null }, void>({
      query: () => ({ url: "/tenancy/logo/", method: "DELETE" }),
      invalidatesTags: [{ type: "Tenant", id: "LOGO" }, "Manifest"],
    }),
    requestVerticalChange: build.mutation<
      VerticalChangeRequest,
      VerticalChangeRequestBody
    >({
      query: (body) => ({
        url: "/tenancy/vertical/",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "VerticalChange", id: "STATUS" }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetVerticalStatusQuery,
  useRequestVerticalChangeMutation,
  useGetTenantCoverQuery,
  useUploadTenantCoverMutation,
  useDeleteTenantCoverMutation,
  useGetTenantLogoQuery,
  useUploadTenantLogoMutation,
  useDeleteTenantLogoMutation,
} = tenancyApi;
